/**
 * Equity Card Formatter
 *
 * Template-based equity card system for per-class shareholder card rendering.
 * Reuses HeadlineFormatter's template processing infrastructure.
 *
 * =============================================================================
 * TEMPLATE SYNTAX (Same as HeadlineFormatter)
 * =============================================================================
 *
 * 1. SIMPLE VARIABLE INTERPOLATION
 *    {{variableName}}
 *    Example: "{{youownLabel}}: {{owned}} {{sharesType}}" → "You Own: 1,000 shares"
 *
 * 2. EXISTENCE CHECK - Show content only if variable exists and is non-zero
 *    {{#if variableName}}content{{/if}}
 *    Example: "{{#if hasVesting}}Your plan{{else}}You Own{{/if}}"
 *
 * 3. THRESHOLD CONDITIONAL - Compare variable to a number
 *    {{#if variableName > threshold}}trueValue{{else}}falseValue{{/if}}
 *    Operators: >, >=, <, <=, ==, !=
 *
 * 4. NUMBER FORMATTING - Control decimal places for currency values
 *    {{variableName | dollars}}  - Rounded to whole dollars
 *    {{variableName | cents}}    - Show dollars and cents (2 decimal places)
 *
 * 5. MATH EXPRESSIONS - Simple arithmetic with variables (left-to-right evaluation)
 *    {{var1 + var2}}            - Addition
 *    {{var1 - var2}}            - Subtraction
 *    {{var1 - var2 - var3}}     - Chained operations (left-to-right)
 *    Example: "{{eligible - owned - committed}}" → "0" (if eligible=10, owned=3, committed=7)
 *
 * =============================================================================
 * AVAILABLE VARIABLES
 * =============================================================================
 *
 * User share data:
 *   owned          - Total shares owned
 *   approved       - Vested/approved shares
 *   unapproved     - Owned minus approved (max 0)
 *   committed      - Committed capital
 *   tranches       - Number of tranches
 *   expDate        - Expiration date
 *   orig           - Original grant (bad actors)
 *
 * Client equity config:
 *   name           - Class display name
 *   sharesType     - "shares", "units", "RSUs"
 *   youownLabel    - Custom holdings label
 *   costbasisLabel - Custom cost basis label
 *
 * Calculated values:
 *   basis          - Cost basis
 *   value          - Current value
 *   interest       - Accrued interest
 *   uncalled       - Approved - owned (or committed)
 *
 * Calculator values (when available from PrivateCompany409A or other calculators):
 *   vested         - Vested share count
 *   unvested       - Unvested share count (totalTarget - vested, or 0 if no schedule)
 *   totalTarget    - Total target shares from vesting schedule
 *   vestedValue    - Dollar value of vested shares
 *   unvestedValue  - Dollar value of unvested shares
 *   ownedValue     - Dollar value of owned shares (owned × per-share value)
 *   approvedValue  - Dollar value of approved shares (approved × per-share value)
 *   unapprovedValue - Dollar value of unapproved shares (unapproved × per-share value)
 *   sharePrice     - Current share price
 *   strikePrice    - Strike price for options
 *
 * Derived values:
 *   hasVesting     - Boolean: user has vesting schedule
 *   hasCalculator  - Boolean: calculator data is available
 *   isDebt         - Boolean: has interestRate
 *   vestedPercent  - vested / owned * 100
 *   uncalledLabel  - "Uncalled Capital" or "Unvested Equity"
 *   tradeAction    - "sell", "purchase", or "trade"
 *
 * Stock status values (Fishbeck-style eligibility, only present if stockStatus provided):
 *   stockStatus    - User's stock status (e.g., "10", "100", "Associate")
 *   eligible       - Maximum shares user can own (derived from stockStatus if numeric,
 *                    or owned for text status like "Associate", "Senior Associate", "Principal")
 *
 * Aggregate variables (when aggregates data is provided):
 *   totalClassShares  - Total shares/units in the class (for % ownership calculation)
 *   totalClassValue   - Total value of all shares in the class
 *   shareholderCount  - Number of shareholders in this class
 *   ownershipPercent  - User's % ownership = (owned / totalClassShares) * 100
 *
 * User identity:
 *   userid           - User's email/ID
 *   unitholderID     - Same as userid (alias for compatibility)
 *
 * =============================================================================
 * PATH-BASED VARIABLES (direct access in templates)
 * =============================================================================
 *
 * Templates can directly access data using path notation:
 *
 *   {{user.idNumber}}        → userData.clients[client].idNumber
 *   {{user.status}}          → userData.clients[client].status
 *   {{shares.eligible}}      → userData.clients[client].shares[classKey].eligible
 *   {{config.totalShares}}   → clientData.equity[classKey].totalShares
 *   {{aggregate.totalOwned}} → aggregates.totalOwned
 *
 * With formatting:
 *   {{user.idNumber | raw}}
 *   {{aggregate.totalOwned | number}}
 *   {{config.someValue | currency}}
 *
 * In conditionals:
 *   {{#if user.idNumber}}ID: {{user.idNumber}}{{/if}}
 *
 * =============================================================================
 * CUSTOM COMPUTED VARIABLES (via configuration)
 * =============================================================================
 *
 * For computed variables (formulas), use clientData.equity[key].templateVariables:
 * {
 *   "Common": {
 *     "templateVariables": {
 *       "customCalc": {
 *         "formula": "(owned / totalClassShares) * 100",
 *         "type": "percentage"
 *       }
 *     }
 *   }
 * }
 *
 * =============================================================================
 */

import { HeadlineFormatter } from './HeadlineFormatter.js';
import { TemplateUtils } from '../helpers/TemplateUtils.js';
import { VariableProvider } from '../providers/VariableProvider.js';

const EquityCardFormatter = {

  // Client-specific number formatting options (e.g., BW uses 4 decimal places)
  // Set by caller before rendering. null = default JS toLocaleString behavior.
  numberFormatOptions: null,

  // ========================================
  // VARIABLE TYPE DEFINITIONS
  // ========================================
  // Determines how each variable is formatted

  variableTypes: {
    // Share counts - formatted as plain numbers with commas
    owned: 'number',
    approved: 'number',
    unapproved: 'number',   // Owned minus approved
    vested: 'number',        // Vested share count (from calculator)
    unvested: 'number',      // Unvested share count (totalTarget - vested, or 0 if no schedule)
    totalTarget: 'number',   // Total target shares from vesting schedule
    orig: 'number',
    tranches: 'number',

    // Currency values
    committed: 'currency',
    basis: 'currency',
    value: 'currency',
    interest: 'currency',
    uncalled: 'currency',
    vestedValue: 'currency',    // Value of vested shares (from calculator)
    unvestedValue: 'currency',  // Value of unvested shares (from calculator)
    ownedValue: 'currency',     // owned × per-share value
    approvedValue: 'currency',  // approved × per-share value
    unapprovedValue: 'currency', // unapproved × per-share value
    sharePrice: 'currency',     // Current share price (from calculator)
    strikePrice: 'currency',    // Strike price for options (from calculator)

    // Income allocations (tax basis + market value)
    taxBasis: 'currency',
    incomeAllocations: 'currency',
    contributions: 'currency',
    distributions: 'currency',
    marketValue: 'currency',
    currentValue: 'currency',
    unrealizedGain: 'currency',

    // Percentage values
    vestedPercent: 'percentage',

    // Text/label variables (raw, will be HTML-escaped)
    name: 'raw',
    sharesType: 'raw',
    youownLabel: 'raw',
    costbasisLabel: 'raw',
    uncalledLabel: 'raw',
    tradeAction: 'raw',
    expDate: 'raw',

    // Boolean flags (used in conditionals, not for display)
    hasVesting: 'raw',
    isDebt: 'raw',
    hasCalculator: 'raw',

    // Stock status / eligibility (Fishbeck-style)
    stockStatus: 'raw',
    eligible: 'number',

    // Aggregate variables (cap table totals)
    totalClassShares: 'number',
    totalClassValue: 'currency',
    shareholderCount: 'number',
    ownershipPercent: 'percentage',

    // User identifiers
    userid: 'raw',
    unitholderID: 'raw'
  },

  // ========================================
  // DEFAULT TEMPLATES
  // ========================================
  // Used when no custom template is configured
  // Lines are numbered for simplicity - content is defined by template

  defaultTemplates: {
    line1: '{{youownLabel}}: {{owned}} {{sharesType}}',
    line2: '',
    line3: '',
    line4: ''
  },

  /**
   * Format a single value based on its type
   * @param {*} value - The value to format
   * @param {string} type - 'currency', 'number', 'percentage', or 'raw'
   * @param {string} [formatOverride] - Optional format override ('dollars', 'cents')
   * @returns {string} - Formatted string (HTML-safe)
   */
  formatValue: function(value, type, formatOverride) {
    if (value === undefined || value === null) return '';

    switch (type) {
      case 'currency':
        if (typeof value !== 'number' || isNaN(value)) return '';
        // Check for format override
        if (formatOverride === 'cents') {
          return '$' + value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        // Default: rounded dollars
        return '$' + value.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });

      case 'number':
        if (typeof value !== 'number' || isNaN(value)) return '';
        return value.toLocaleString('en-US', this.numberFormatOptions || undefined);

      case 'percentage':
        if (typeof value !== 'number' || isNaN(value)) return '';
        return value.toFixed(2) + '%';

      default:
        // Escape HTML for raw string values to prevent XSS
        return TemplateUtils.escapeHtml(String(value));
    }
  },

  /**
   * Safely get a variable value, protecting against prototype pollution
   * Delegates to TemplateUtils.safeGetVar
   */
  safeGetVar: function(vars, varName) {
    return TemplateUtils.safeGetVar(vars, varName);
  },

  /**
   * Build a variable map from user share data, client equity config, and calculator results
   * @param {Object} options
   * @param {Object} options.userShares - User's share data for this class (uD[key])
   * @param {Object} options.equityConfig - Client equity config for this class (cD.equity[key])
   * @param {number} options.bases - Cost basis value
   * @param {number} options.values - Current value
   * @param {number} options.interest - Accrued interest (for ACC clients)
   * @param {Object} options.calculatorData - Per-class calculator breakdown (from window.lastCalculationResult.breakdown)
   * @param {string} [options.stockStatus] - User's stock status for eligibility calculation (e.g., "10", "100", "Associate")
   * @param {Object} [options.userData] - Full user data object (for configured variables)
   * @param {Object} [options.clientData] - Full client data object (for configured variables)
   * @param {Object} [options.aggregates] - Cap table aggregates for this class (totalOwned, shareholderCount, etc.)
   * @param {string} [options.classKey] - Share class key
   * @param {string} [options.client] - Client code (e.g., "portblakely") for path-based variable resolution
   * @returns {Object} - Flat map of variable names to values
   */
  buildVariableMap: function(options) {
    const { userShares = {}, equityConfig = {}, bases = 0, values = 0, interest = 0, calculatorData = null, stockStatus = null, userData = null, clientData = null, aggregates = null, classKey = '', client: clientCode = '' } = options;

    // Resolve client code once for all path-based variable lookups
    const client = clientCode || clientData?.code || clientData?.name || '';
    const userClientData = userData?.clients?.[client] || {};
    const userShareData = userClientData?.shares?.[classKey] || {};

    const owned = Number(userShares.owned || 0);
    const approved = Number(userShares.approved || 0);
    const committed = Number(userShares.committed || 0);

    // Calculate uncalled: use committed if set, otherwise approved - owned
    let uncalled = committed > 0 ? committed : Math.max(0, approved - owned);

    // Get calculator values if available
    const hasCalculator = !!calculatorData;
    const vested = hasCalculator ? Number(calculatorData.vested || 0) : approved;
    const totalTarget = hasCalculator ? Number(calculatorData.totalTarget || 0) : 0;

    // Unvested comes purely from the schedule: totalTarget - vested
    // If no schedule (totalTarget = 0), unvested is 0
    const unvested = totalTarget > 0 ? (totalTarget - vested) : 0;

    const unapproved = Math.max(0, owned - approved);

    const vars = {
      // User share data
      owned,
      approved,
      unapproved,
      committed,
      tranches: userShares.tranches || 0,
      expDate: userShares.expDate || '',
      orig: Number(userShares.orig || 0),

      // Vesting counts (from calculator or fallback to approved)
      vested,
      unvested: Math.max(0, unvested),
      totalTarget,

      // Client equity config labels
      name: equityConfig.name || '',
      sharesType: equityConfig.sharesType || 'shares',
      youownLabel: equityConfig.youownLabel || (userShares.vesting ? 'Your plan' : 'You Own'),
      costbasisLabel: equityConfig.costbasisLabel || 'Cost Basis',

      // Calculated values
      basis: bases,
      value: values,
      interest: interest,
      uncalled: uncalled,

      // Derived boolean/computed values
      hasVesting: !!userShares.vesting,
      hasCalculator: hasCalculator,
      isDebt: !!equityConfig.interestRate,
      vestedPercent: owned > 0 ? (vested / owned) * 100 : 0,
      uncalledLabel: equityConfig.uncalledLabel || 'Uncalled',
      tradeAction: equityConfig.tradeAction || 'trade'
    };

    // Stock status eligibility calculation (Fishbeck-style)
    // Only add these variables when stockStatus is provided
    if (stockStatus) {
      vars.stockStatus = stockStatus;

      // Check if stockStatus is numeric (e.g., "10", "100")
      const numericStatus = parseInt(stockStatus, 10);
      const isNumericStatus = !isNaN(numericStatus) && String(numericStatus) === stockStatus;

      if (isNumericStatus) {
        // For numeric status: eligible = the status value (it's the cap)
        vars.eligible = numericStatus;
      } else {
        // For text status (Associate, Senior Associate, Principal, etc.):
        // eligible = owned (they own what they're eligible for)
        vars.eligible = owned;
      }
    }

    // Add calculator values if available
    if (hasCalculator) {
      if (calculatorData.vestedValue !== undefined) vars.vestedValue = calculatorData.vestedValue;
      if (calculatorData.unvestedValue !== undefined) vars.unvestedValue = calculatorData.unvestedValue;
      if (calculatorData.sharePrice !== undefined) vars.sharePrice = calculatorData.sharePrice;
      if (calculatorData.strikePrice !== undefined) vars.strikePrice = calculatorData.strikePrice;
      if (calculatorData.value !== undefined) vars.value = calculatorData.value;

      // Owned/Approved/Unapproved dollar values (shares * per-share value)
      const perShareVal = (calculatorData.sharePrice || 0) - (calculatorData.strikePrice || 0);
      vars.ownedValue = owned * perShareVal;
      vars.approvedValue = approved * perShareVal;
      vars.unapprovedValue = unapproved * perShareVal;

      // Income allocations and tax basis values
      if (calculatorData.taxBasis !== undefined) vars.taxBasis = calculatorData.taxBasis;
      if (calculatorData.incomeAllocations !== undefined) vars.incomeAllocations = calculatorData.incomeAllocations;
      if (calculatorData.contributions !== undefined) vars.contributions = calculatorData.contributions;
      if (calculatorData.distributions !== undefined) vars.distributions = calculatorData.distributions;
      if (calculatorData.marketValue !== undefined) vars.marketValue = calculatorData.marketValue;
      if (calculatorData.currentValue !== undefined && vars.marketValue === undefined) {
        vars.marketValue = calculatorData.currentValue;
      }
      if (calculatorData.unrealizedGain !== undefined) vars.unrealizedGain = calculatorData.unrealizedGain;
    }

    // Apply aggregate data (cap table totals)
    if (aggregates) {
      if (aggregates.totalOwned !== undefined) {
        vars.totalClassShares = Number(aggregates.totalOwned);
      }
      if (aggregates.totalValue !== undefined) {
        vars.totalClassValue = Number(aggregates.totalValue);
      }
      if (aggregates.shareholderCount !== undefined) {
        vars.shareholderCount = Number(aggregates.shareholderCount);
      }

      // Compute ownershipPercent if we have the data
      if (vars.owned !== undefined && vars.totalClassShares && vars.totalClassShares > 0) {
        vars.ownershipPercent = (vars.owned / vars.totalClassShares) * 100;
      }
    }

    // Apply user data variables
    if (userData) {
      vars.userid = userData.userid || '';
      vars.unitholderID = userData.userid || '';
    }

    // Apply configured variables from templateVariables configs
    // Class-level config takes precedence over portfolio-level
    const dataSources = { userClientData, userShareData, equityConfig, aggregates, userData, clientData, client };
    this._applyTemplateVariables(vars, equityConfig.templateVariables, dataSources);
    this._applyTemplateVariables(vars, clientData?.templateVariables, dataSources, true);

    // Store context for path-based variable resolution in templates
    // This allows templates to use {{user.idNumber}}, {{shares.eligible}}, etc.
    vars._context = {
      user: userClientData,
      shares: userShareData,
      config: equityConfig || {},
      aggregate: aggregates || {}
    };

    return vars;
  },

  /**
   * Apply templateVariables config to vars map
   * Supports shorthand paths (user., shares., config., aggregate.) and full paths
   *
   * @param {Object} vars - Variable map to populate
   * @param {Object} config - templateVariables config object
   * @param {Object} sources - Data sources for path resolution
   * @param {boolean} [skipExisting=false] - If true, don't overwrite existing vars
   * @private
   */
  _applyTemplateVariables: function(vars, config, sources, skipExisting = false) {
    if (!config || typeof config !== 'object') return;

    const { userClientData, userShareData, equityConfig, aggregates, userData, clientData, client } = sources;

    Object.entries(config).forEach(([varName, varConfig]) => {
      if (skipExisting && vars[varName] !== undefined) return;
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName)) return;
      if (typeof varConfig !== 'object' || varConfig === null) return;

      const { source, formula } = varConfig;

      // Compute from formula
      if (formula) {
        const computed = VariableProvider.evaluateFormula(formula, vars);
        if (computed !== null) {
          vars[varName] = computed;
        }
        return;
      }

      // Resolve from source path
      if (source) {
        let value;
        if (source.startsWith('user.')) {
          value = VariableProvider.resolvePath(userClientData, source.replace('user.', ''), {});
        } else if (source.startsWith('shares.')) {
          value = VariableProvider.resolvePath(userShareData, source.replace('shares.', ''), {});
        } else if (source.startsWith('config.')) {
          value = VariableProvider.resolvePath(equityConfig, source.replace('config.', ''), {});
        } else if (source.startsWith('aggregate.')) {
          value = VariableProvider.resolvePath(aggregates || {}, source.replace('aggregate.', ''), {});
        } else if (source.startsWith('userData.')) {
          value = VariableProvider.resolvePath(userData || {}, source.replace('userData.', ''), { client });
        } else if (source.startsWith('clientData.')) {
          value = VariableProvider.resolvePath(clientData || {}, source.replace('clientData.', ''), { client });
        }
        if (value !== undefined) {
          vars[varName] = value;
        }
      }
    });
  },

  /**
   * Resolve a path-based variable like "user.idNumber" or "shares.eligible"
   * @param {string} path - Dot-notation path with prefix (user., shares., config., aggregate.)
   * @param {Object} context - Context object with data sources
   * @returns {*} - Resolved value or undefined
   */
  resolvePathVariable: function(path, context) {
    if (!path || !context) return undefined;

    const parts = path.split('.');
    if (parts.length < 2) return undefined;

    const prefix = parts[0];
    const remainingPath = parts.slice(1).join('.');

    const source = context[prefix];
    if (!source) return undefined;

    return VariableProvider.resolvePath(source, remainingPath, {});
  },

  /**
   * Format a template string with variables
   * Uses TemplateUtils for shared processing (conditionals, math)
   *
   * Supports both simple variables and path-based variables:
   *   {{varName}}           - Simple variable from vars
   *   {{user.idNumber}}     - Path-based: userData.clients[client].idNumber
   *   {{shares.eligible}}   - Path-based: userData.clients[client].shares[class].eligible
   *   {{config.totalShares}} - Path-based: clientData.equity[class].totalShares
   *   {{aggregate.totalOwned}} - Path-based: aggregates.totalOwned
   *
   * @param {string} template - Template string with {{variables}}
   * @param {Object} vars - Variable map (includes _context for path resolution)
   * @returns {string} - Formatted string
   */
  formatWithVars: function(template, vars) {
    if (!template) return '';

    const self = this;
    const context = vars._context || {};

    // Create working copy to avoid mutating original vars
    const workingVars = Object.assign({}, vars);

    // Helper to get value - handles both simple vars and path-based vars
    const getValue = function(varExpr) {
      // Check if it's a path (contains . and starts with known prefix)
      if (varExpr.includes('.') && /^(user|shares|config|aggregate)\./.test(varExpr)) {
        return self.resolvePathVariable(varExpr, context);
      }
      // Simple variable
      return TemplateUtils.safeGetVar(workingVars, varExpr);
    };

    // Helper to get type for formatting
    const getType = function(varExpr, formatOverride) {
      if (formatOverride) {
        // Map format modifiers to types
        if (formatOverride === 'dollars' || formatOverride === 'cents' || formatOverride === 'currency') return 'currency';
        if (formatOverride === 'number') return 'number';
        if (formatOverride === 'percentage') return 'percentage';
        if (formatOverride === 'raw') return 'raw';
      }
      // For simple vars, use variableTypes
      if (!varExpr.includes('.')) {
        return self.variableTypes[varExpr] || 'raw';
      }
      // For paths, default to raw (can be overridden with pipe)
      return 'raw';
    };

    // Step 1: Pre-resolve path variables for conditionals
    // Add resolved path vars to working copy for conditional processing
    const pathVarRegex = /\{\{#if\s+((?:user|shares|config|aggregate)\.[a-zA-Z_.]+)/g;
    let match;
    while ((match = pathVarRegex.exec(template)) !== null) {
      const pathVar = match[1];
      if (workingVars[pathVar] === undefined) {
        const value = self.resolvePathVariable(pathVar, context);
        if (value !== undefined) {
          workingVars[pathVar] = value;
        }
      }
    }

    // Step 2: Process conditionals ({{#if var}}...{{/if}})
    template = HeadlineFormatter.processConditionals(template, workingVars);

    // Step 3: Process format modifiers and filters {{varName | modifier}} or {{path.var | filter}}
    // Format modifiers: dollars, cents, currency, number, percentage, raw
    // Filters: isNumeric, toNumber, isText, uppercase, lowercase
    template = template.replace(/\{\{([\w.]+)\s*\|\s*(\w+)\}\}/g, function(_match, varExpr, modifier) {
      let value = getValue(varExpr);

      // Check if it's a filter first
      if (TemplateUtils.filters[modifier]) {
        value = TemplateUtils.applyFilter(value, modifier);
        // For toNumber filter, format as number
        if (modifier === 'toNumber' && typeof value === 'number') {
          return self.formatValue(value, 'number');
        }
        // For boolean filters like isNumeric/isText, just return the value
        return self.formatValue(value, 'raw');
      }

      // Otherwise treat as format modifier
      const type = getType(varExpr, modifier);
      return self.formatValue(value, type, modifier);
    });

    // Step 4: Process math expressions {{var1 + var2}}, {{var1 - var2 - var3}}, etc.
    template = TemplateUtils.processMathExpressions(template, workingVars);

    // Step 5: Process simple variable interpolation {{varName}} or {{path.var}}
    return template.replace(/\{\{([\w.]+)\}\}/g, function(_match, varExpr) {
      const value = getValue(varExpr);
      const type = getType(varExpr);
      return self.formatValue(value, type);
    });
  },

  /**
   * Get template with fallback chain:
   * 1. Per-class template (classConfig.line[N]_template)
   * 2. Built-in default (this.defaultTemplates.line[N])
   *
   * @param {string} lineKey - Line key ('line1', 'line2', 'line3', 'line4')
   * @param {Object} classConfig - Per-class equity config (cD.equity[key])
   * @returns {string} - Template to use
   */
  getTemplate: function(lineKey, classConfig) {
    // Check per-class override first
    const classTemplate = classConfig?.[`${lineKey}_template`];
    if (classTemplate) return classTemplate;

    // Fall back to built-in default
    return this.defaultTemplates[lineKey] || '';
  },

  /**
   * Render a single template line
   * @param {string} lineKey - Line key ('line1', 'line2', 'line3', 'line4')
   * @param {Object} classConfig - Per-class equity config
   * @param {Object} vars - Variable map from buildVariableMap
   * @returns {string} - Rendered line (empty string if template is empty)
   */
  renderLine: function(lineKey, classConfig, vars) {
    const template = this.getTemplate(lineKey, classConfig);
    if (!template) return '';
    return this.formatWithVars(template, vars);
  }
};


export { EquityCardFormatter };
